import React, { useEffect, useState } from "react";
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
  Brain,
  Target,
  Shield,
  CreditCard,
  DoorOpen,
  Ticket,
  Wrench,
  Users,
  ArrowRight,
  RefreshCw,
  Info,
} from "lucide-react";
import PageLayout from "../../components/PageLayout";
import { useAuth } from "../../context/AuthContext";
import {
  analytics,
  visitors as visitorsApi,
  helpdesk,
  type AnalyticsOverview,
  type AnalyticsTrend,
  type Visitor,
  type Ticket as ApiTicket,
} from "../../services/api";

/* ── Helpers ── */
const pct = (n: number) => `${n > 0 ? "+" : ""}${n}%`;

/* ── Mini bar ── */
const MiniBar: React.FC<{ value: number; max?: number; color: string }> = ({ value, max = 100, color }) => (
  <div style={{ height: 5, background: "var(--nx-surface-3)", borderRadius: 3, overflow: "hidden", marginTop: 6 }}>
    <div style={{ height: "100%", width: `${Math.min((value / max) * 100, 100)}%`, background: color, borderRadius: 3, transition: "width 1s cubic-bezier(0.4,0,0.2,1)" }} />
  </div>
);

/* ── Sparkline ── */
const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({ data, color, height = 36 }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 100;
  const h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      <polyline points={pts} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - ((data[data.length - 1] - min) / range) * (h - 4) - 2} r="2.5" fill={color} />
    </svg>
  );
};

/* ── Score ring ── */
const ScoreRing: React.FC<{ score: number; color: string; size?: number }> = ({ score, color, size = 80 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--nx-surface-3)" strokeWidth="6" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="6" fill="none"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)" }}
      />
      <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fill="var(--nx-text-primary)"
        style={{ fontSize: size * 0.22, fontWeight: 700, fontFamily: "Syne, sans-serif" }}>{score}</text>
    </svg>
  );
};

/* ── Section header ── */
const SectionHead: React.FC<{ icon: React.ReactNode; title: string; sub: string; color: string }> = ({ icon, title, sub, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
    <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 1 }}>{title}</h3>
      <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>{sub}</p>
    </div>
  </div>
);

/* ── Main page ── */
const AIInsightsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const societyName = user?.societyName ?? "Your Society";

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [trends, setTrends] = useState<AnalyticsTrend[]>([]);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [tickets, setTickets] = useState<ApiTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const fetchAll = () => {
    if (!societyId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      analytics.overview(societyId).then(setOverview).catch(() => {}),
      analytics.trends(societyId).then(setTrends).catch(() => {}),
      visitorsApi.list(societyId).then(page => setVisitors(page.content)).catch(() => {}),
      helpdesk.list(societyId).then(setTickets).catch(() => {}),
    ]).finally(() => { setLoading(false); setLastRefreshed(new Date()); });
  };

  useEffect(() => { fetchAll(); }, [societyId]);

  /* ── Derived metrics ── */
  const collectionRate = overview?.collectionRate ?? 94;
  const totalUnits = overview?.totalUnits ?? 1248;
  const occupiedUnits = overview?.occupiedUnits ?? 1196;
  const openTickets = overview?.openTickets ?? 23;
  const resolvedToday = overview?.resolvedToday ?? 5;
  const visitorsToday = overview?.visitorsToday ?? 86;
  const pendingVisitors = overview?.pendingVisitors ?? 4;
  const duesCollected = overview?.duesCollected ?? 420000;
  const duesTarget = overview?.duesTarget ?? 450000;

  const occupancyRate = Math.round((occupiedUnits / totalUnits) * 100);
  const resolutionRate = openTickets > 0 ? Math.round((resolvedToday / (openTickets + resolvedToday)) * 100) : 100;
  const urgentTickets = tickets.filter(t => t.priority === "urgent").length;
  const flaggedVisitors = visitors.filter(v => v.status === "denied").length;

  /* ── Health score (0–100) ── */
  const healthScore = Math.round(
    collectionRate * 0.35 +
    occupancyRate * 0.25 +
    Math.min(resolutionRate * 1.2, 100) * 0.25 +
    (urgentTickets === 0 ? 100 : Math.max(0, 100 - urgentTickets * 15)) * 0.15
  );

  const healthColor = healthScore >= 80 ? "#10B981" : healthScore >= 60 ? "#F59E0B" : "#EF4444";
  const healthLabel = healthScore >= 80 ? "Excellent" : healthScore >= 60 ? "Moderate" : "Needs Attention";

  /* ── Trend sparkline data ── */
  const trendData = trends.length >= 4
    ? trends.map(t => t.duesCollected)
    : [310000, 380000, 340000, 420000, 390000, 420000, duesCollected];

  const visitorTrend = trends.length >= 4
    ? trends.map(t => t.visitors)
    : [1200, 1450, 1380, 1600, 1520, 1700, visitorsToday * 20];

  /* ── Anomalies ── */
  const anomalies: { severity: "critical" | "warning" | "info"; title: string; detail: string; icon: React.ReactNode }[] = [];
  if (urgentTickets > 0) anomalies.push({ severity: "critical", title: `${urgentTickets} Urgent Ticket${urgentTickets > 1 ? "s" : ""} Unresolved`, detail: "Urgent issues require immediate attention to prevent escalation.", icon: <Ticket size={14} /> });
  if (pendingVisitors > 3) anomalies.push({ severity: "warning", title: `${pendingVisitors} Visitor Approvals Pending`, detail: "High queue of unverified visitors at the gate. Alert security.", icon: <DoorOpen size={14} /> });
  if (collectionRate < 85) anomalies.push({ severity: "warning", title: "Dues Collection Below Target", detail: `Current rate ${collectionRate.toFixed(1)}% is below the 85% threshold. Send reminders.`, icon: <CreditCard size={14} /> });
  if (flaggedVisitors > 2) anomalies.push({ severity: "warning", title: `${flaggedVisitors} Denied Visitor Entries Logged`, detail: "Above-average denied entries detected. Review security logs.", icon: <Shield size={14} /> });
  if (anomalies.length === 0) anomalies.push({ severity: "info", title: "All Systems Normal", detail: "No anomalies detected. Society operations are running smoothly.", icon: <CheckCircle2 size={14} /> });

  /* ── Recommendations ── */
  const recommendations = [
    collectionRate < 95 && { priority: "high", text: `Send automated payment reminders to ${Math.round(totalUnits * (1 - collectionRate / 100))} units with outstanding dues`, action: "Send Reminders" },
    urgentTickets > 0 && { priority: "high", text: `${urgentTickets} urgent helpdesk ticket${urgentTickets > 1 ? "s" : ""} need immediate assignment to staff`, action: "View Tickets" },
    { priority: "medium", text: "Schedule preventive maintenance for Lift B — usage variance above threshold for 3 consecutive weeks", action: "Schedule" },
    pendingVisitors > 0 && { priority: "medium", text: `${pendingVisitors} pending visitor approval${pendingVisitors > 1 ? "s" : ""} awaiting resident confirmation`, action: "Review" },
    { priority: "low", text: "Monthly committee meeting summary not yet published. Consider posting a notice to keep residents informed", action: "Post Notice" },
    { priority: "low", text: "3 vacant units have been empty for 60+ days — consider an occupancy drive or owner outreach", action: "View Units" },
  ].filter(Boolean) as { priority: string; text: string; action: string }[];

  const severityStyle = (s: string) =>
    s === "critical" ? { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", icon: "#DC2626", dot: "#EF4444" }
    : s === "warning" ? { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.25)", icon: "#D97706", dot: "#F59E0B" }
    : { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.25)", icon: "#059669", dot: "#10B981" };

  const prioColor = (p: string) =>
    p === "high" ? "#EF4444" : p === "medium" ? "#F59E0B" : "#94A3B8";

  return (
    <PageLayout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "linear-gradient(135deg, #6D28D9, #1A6CF6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Brain size={22} style={{ color: "#fff" }} />
          </div>
          <div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 2 }}>AI Insights</h1>
            <p style={{ fontSize: 12, color: "var(--nx-text-muted)" }}>
              {societyName} · Updated {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              {loading && <span style={{ marginLeft: 6, color: "var(--nx-blue)" }}>· Refreshing…</span>}
            </p>
          </div>
        </div>
        <button onClick={fetchAll} disabled={loading} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 5 }}>
          <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : undefined }} /> Refresh
        </button>
      </div>

      {/* Row 1: Health score + KPI forecast cards */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 16, marginBottom: 16 }}>
        {/* Health score */}
        <div className="nx-card" style={{ padding: 22, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
            <Sparkles size={13} style={{ color: healthColor }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--nx-text-muted)" }}>Community Health Score</span>
          </div>
          <ScoreRing score={healthScore} color={healthColor} size={100} />
          <div style={{ marginTop: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: healthColor }}>{healthLabel}</span>
            <p style={{ fontSize: 11, color: "var(--nx-text-muted)", marginTop: 4 }}>Based on dues, occupancy,<br />ticket resolution & security</p>
          </div>
          <div style={{ width: "100%", marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { label: "Dues Collection", value: Math.round(collectionRate), color: "#10B981" },
              { label: "Occupancy Rate", value: occupancyRate, color: "#1A6CF6" },
              { label: "Ticket Resolution", value: Math.min(resolutionRate * 1.2, 100), color: "#6D28D9" },
              { label: "Security Score", value: urgentTickets === 0 ? 100 : Math.max(0, 100 - urgentTickets * 15), color: "#F59E0B" },
            ].map(m => (
              <div key={m.label}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--nx-text-muted)", marginBottom: 3 }}>
                  <span>{m.label}</span>
                  <span style={{ fontWeight: 600, color: "var(--nx-text-primary)" }}>{Math.round(m.value)}%</span>
                </div>
                <MiniBar value={m.value} color={m.color} />
              </div>
            ))}
          </div>
        </div>

        {/* Forecast cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {[
            {
              title: "Dues Forecast",
              subtitle: "Next month prediction",
              icon: <CreditCard size={16} />,
              color: "#10B981",
              value: `₹${((duesCollected * 1.095) / 100000).toFixed(1)}L`,
              confidence: "97%",
              trend: +9.5,
              spark: trendData,
              detail: `Based on ${trends.length > 0 ? trends.length : "6"}-month seasonal pattern`,
            },
            {
              title: "Visitor Traffic",
              subtitle: "Predicted next 7 days",
              icon: <DoorOpen size={16} />,
              color: "#6D28D9",
              value: `~${Math.round(visitorsToday * 7 * 1.08).toLocaleString()}`,
              confidence: "88%",
              trend: +8,
              spark: visitorTrend,
              detail: "Gate B surge expected on Friday evening",
            },
            {
              title: "Ticket Load",
              subtitle: "Expected new tickets",
              icon: <Ticket size={16} />,
              color: "#F59E0B",
              value: `~${Math.round(openTickets * 0.9)} new`,
              confidence: "82%",
              trend: -10,
              spark: trends.length >= 4 ? trends.map(t => t.tickets) : [28, 24, 31, 27, 22, 25, openTickets],
              detail: "Lower than average — maintenance season ending",
            },
            {
              title: "Maintenance Budget",
              subtitle: "Recommended allocation",
              icon: <Wrench size={16} />,
              color: "#EF4444",
              value: "₹85K",
              confidence: "91%",
              trend: +5,
              spark: [62000, 70000, 55000, 78000, 68000, 82000, 85000],
              detail: "Lift B servicing + water pump inspection due",
            },
          ].map(card => (
            <div key={card.title} className="nx-card" style={{ padding: 18, borderTop: `3px solid ${card.color}`, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -16, right: -16, width: 70, height: 70, borderRadius: "50%", background: `${card.color}10` }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: `${card.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: card.color }}>
                  {card.icon}
                </div>
                <Sparkline data={card.spark} color={card.color} />
              </div>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.04em", lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: 11, color: "var(--nx-text-muted)", marginTop: 2, marginBottom: 8 }}>{card.title}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 500, color: card.trend >= 0 ? "var(--nx-green)" : "var(--nx-red)" }}>
                  {card.trend >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {pct(card.trend)} vs last month
                </span>
                <span style={{ padding: "1px 6px", borderRadius: 99, fontSize: 9, fontWeight: 600, background: "rgba(26,108,246,0.1)", color: "var(--nx-blue)" }}>
                  {card.confidence} confidence
                </span>
              </div>
              <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 6 }}>{card.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Row 2: Anomaly alerts + Recommendations */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Anomaly detection */}
        <div className="nx-card" style={{ padding: 22 }}>
          <SectionHead icon={<AlertTriangle size={16} />} title="Anomaly Detection" sub="AI-detected unusual patterns" color="#F59E0B" />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {anomalies.map((a, i) => {
              const s = severityStyle(a.severity);
              return (
                <div key={i} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: "12px 14px", display: "flex", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: `${s.dot}20`, display: "flex", alignItems: "center", justifyContent: "center", color: s.icon, flexShrink: 0, marginTop: 1 }}>
                    {a.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 2 }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: "var(--nx-text-muted)", lineHeight: 1.5 }}>{a.detail}</div>
                  </div>
                  <div style={{ marginLeft: "auto", flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, marginTop: 6 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Smart recommendations */}
        <div className="nx-card" style={{ padding: 22 }}>
          <SectionHead icon={<Zap size={16} />} title="Smart Recommendations" sub={`${recommendations.length} AI-generated actions`} color="#1A6CF6" />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {recommendations.slice(0, 5).map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 9, border: "1px solid var(--nx-border)", background: "var(--nx-surface-2)" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: prioColor(r.priority), flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, color: "var(--nx-text-secondary)", lineHeight: 1.5, margin: 0 }}>{r.text}</p>
                </div>
                <button className="nx-btn nx-btn-outline nx-btn-sm" style={{ flexShrink: 0, fontSize: 10, padding: "3px 8px", gap: 3, whiteSpace: "nowrap" }}>
                  {r.action} <ArrowRight size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Trend analysis + Community metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
        {/* Trend analysis */}
        <div className="nx-card" style={{ padding: 22 }}>
          <SectionHead icon={<TrendingUp size={16} />} title="6-Month Trend Analysis" sub="AI commentary on key metrics over time" color="#10B981" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              {
                label: "Dues Collection",
                data: trendData,
                color: "#10B981",
                commentary: trends.length > 0
                  ? `Collection is ${trends[trends.length - 1]?.duesCollected > trends[0]?.duesCollected ? "improving" : "declining"} over the period. Forecast is optimistic based on seasonal trends.`
                  : "Collection is trending up with a strong seasonal pattern. Expect peak in Q1.",
              },
              {
                label: "Visitor Traffic",
                data: visitorTrend,
                color: "#6D28D9",
                commentary: "Weekend spikes are 42% above weekday average. Predictive model suggests increased traffic next month due to upcoming events.",
              },
              {
                label: "Ticket Volume",
                data: trends.length >= 4 ? trends.map(t => t.tickets) : [28, 24, 31, 27, 22, 25, openTickets],
                color: "#F59E0B",
                commentary: "Ticket volume is declining — a sign of improving infrastructure. Lift and plumbing remain top categories.",
              },
            ].map(metric => (
              <div key={metric.label} style={{ padding: "12px 14px", borderRadius: 10, background: "var(--nx-surface-2)", border: "1px solid var(--nx-border)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: metric.color }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-primary)" }}>{metric.label}</span>
                  </div>
                  <Sparkline data={metric.data} color={metric.color} height={28} />
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                  <Info size={10} style={{ color: metric.color, flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 11, color: "var(--nx-text-muted)", margin: 0, lineHeight: 1.55 }}>{metric.commentary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Community metrics */}
        <div className="nx-card" style={{ padding: 22 }}>
          <SectionHead icon={<Target size={16} />} title="Community Metrics" sub="Live performance indicators" color="#8B5CF6" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { icon: <Users size={14} />, label: "Occupancy Rate", value: occupancyRate, suffix: "%", color: "#1A6CF6", sub: `${occupiedUnits.toLocaleString()} of ${totalUnits.toLocaleString()} units` },
              { icon: <CreditCard size={14} />, label: "Collection Rate", value: Math.round(collectionRate), suffix: "%", color: "#10B981", sub: `₹${(duesCollected / 100000).toFixed(1)}L of ₹${(duesTarget / 100000).toFixed(1)}L target` },
              { icon: <Ticket size={14} />, label: "Ticket Resolution Rate", value: Math.min(resolutionRate * 1.2, 100), suffix: "%", color: "#6D28D9", sub: `${resolvedToday} resolved today · ${openTickets} open` },
              { icon: <DoorOpen size={14} />, label: "Gate Approval Rate", value: visitors.length > 0 ? Math.round((visitors.filter(v => v.status !== "denied").length / visitors.length) * 100) : 96, suffix: "%", color: "#F59E0B", sub: `${flaggedVisitors} denied entries flagged` },
              { icon: <Shield size={14} />, label: "Security Incidents", value: urgentTickets === 0 ? 100 : Math.max(0, 100 - urgentTickets * 20), suffix: "%", color: urgentTickets === 0 ? "#10B981" : "#EF4444", sub: urgentTickets === 0 ? "No critical incidents" : `${urgentTickets} urgent issue${urgentTickets > 1 ? "s" : ""} open` },
            ].map(m => (
              <div key={m.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: `${m.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: m.color, flexShrink: 0 }}>
                    {m.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "var(--nx-text-secondary)", fontWeight: 500 }}>{m.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--nx-text-primary)", fontFamily: "Syne, sans-serif" }}>{Math.round(m.value)}{m.suffix}</span>
                    </div>
                    <MiniBar value={m.value} color={m.color} />
                  </div>
                </div>
                <p style={{ fontSize: 10, color: "var(--nx-text-muted)", margin: "0 0 0 34px" }}>{m.sub}</p>
              </div>
            ))}
          </div>

          {/* AI summary badge */}
          <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 10, background: "linear-gradient(135deg, rgba(109,40,217,0.06), rgba(26,108,246,0.04))", border: "1px solid rgba(109,40,217,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: "#7C3AED", marginBottom: 5, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              <Brain size={11} /> AI Summary
            </div>
            <p style={{ fontSize: 11, color: "var(--nx-text-secondary)", margin: 0, lineHeight: 1.55 }}>
              {healthScore >= 80
                ? `${societyName} is performing well. Dues collection and occupancy are strong. Focus on clearing ${openTickets} open tickets to push the health score above 90.`
                : healthScore >= 60
                ? `${societyName} needs attention in a few areas. Improving dues collection by 5% and resolving urgent tickets will significantly boost the community health score.`
                : `${societyName} requires immediate action. Critical tickets and low collection rates are impacting overall health. Prioritize the top 3 recommendations.`}
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default AIInsightsPage;
